import { useState, useEffect, useCallback, useRef } from 'react'
import { SyncQueueItem, SyncStatus, SyncConflict } from '../types/database.types'
import { SyncProgress } from '../types/database.types'
import { syncService } from '../services/sync.service'
import { useOfflineMode } from '@/providers/OfflineModeProvider'

export function useSync() {
    const { isOfflineDatabaseReady } = useOfflineMode()
    const [isSyncing, setIsSyncing] = useState(false)
    const [syncStatus, setSyncStatus] = useState<SyncStatus>({
        isActive: false,
        lastSync: undefined,
        pendingCount: 0,
        failedCount: 0,
        completedCount: 0,
        totalCount: 0,
        hasConflicts: false
    })
    const [conflicts, setConflicts] = useState<SyncConflict[]>([])
    const [progress, setProgress] = useState<SyncProgress | null>(null)
    // Keep hook order stable across HMR to avoid hook order mismatch
    const hookOrderKeeper = useRef<number>(0)
    hookOrderKeeper.current = hookOrderKeeper.current

    const updateSyncStatus = useCallback(async () => {
        if (!isOfflineDatabaseReady) return
        try {
            const status = await syncService.getSyncStatus()
            setSyncStatus(status)
            setIsSyncing(status.isActive)
        } catch (error) {
            console.warn('Sync status update failed (database not ready):', error)
            // Keep default status when database is not ready
        }
    }, [isOfflineDatabaseReady])

    const addToSyncQueue = useCallback(async (item: Omit<SyncQueueItem, 'id' | 'createdAt' | 'updatedAt' | 'status' | 'retryCount'>): Promise<void> => {
        try {
            await syncService.addToSyncQueue(item)
            await updateSyncStatus()
        } catch (error) {
            console.warn('Failed to add item to sync queue:', error)
        }
    }, [updateSyncStatus])

    const sync = useCallback(async () => {
        if (!isOfflineDatabaseReady) {
            console.warn('Sync requested but offline database is not ready yet. Skipping.')
            return
        }
        setIsSyncing(true)
        try {
            // Use the smarter sync that re-queues failed items and scans for unsynced ones
            await syncService.syncNow()
        } catch (error) {
            console.warn('Sync operation failed:', error)
            // Error is already logged in sync service
        } finally {
            setIsSyncing(false)
            await updateSyncStatus()
        }
    }, [updateSyncStatus, isOfflineDatabaseReady])

    const syncPatient = useCallback(async (_item: SyncQueueItem): Promise<void> => {
        await syncService.sync()
    }, [])

    const syncConsultation = useCallback(async (_item: SyncQueueItem): Promise<void> => {
        await syncService.sync()
    }, [])

    const syncDocument = useCallback(async (_item: SyncQueueItem): Promise<void> => {
        await syncService.sync()
    }, [])

    const getAllConflicts = useCallback(async (): Promise<SyncConflict[]> => {
        if (!isOfflineDatabaseReady) return []
        try {
            const all = await syncService.getAllConflicts()
            setConflicts(all)
            return all
        } catch (error) {
            console.warn('Failed to get conflicts:', error)
            return []
        }
    }, [isOfflineDatabaseReady])

    const resolveConflict = useCallback(async (conflictId: string, resolution: 'use_local' | 'use_remote' | 'merge'): Promise<void> => {
        try {
            await syncService.resolveConflict(conflictId, resolution)
            const remaining = await syncService.getAllConflicts()
            setConflicts(remaining)
        } catch (error) {
            console.warn('Failed to resolve conflict:', error)
        }
    }, [])

    const clearCompletedItems = useCallback(async () => {
        try {
            await syncService.clearCompletedItems()
            await updateSyncStatus()
        } catch (error) {
            console.warn('Failed to clear completed items:', error)
        }
    }, [updateSyncStatus])

    const retryFailedItems = useCallback(async () => {
        try {
            await syncService.retryFailedItems()
            await updateSyncStatus()
            await syncService.sync()
        } catch (error) {
            console.warn('Failed to retry items:', error)
        }
    }, [updateSyncStatus])

    const clearAllOfflineData = useCallback(async () => {
        try {
            await syncService.clearAllOfflineData()
            await updateSyncStatus()
        } catch (error) {
            console.warn('Failed to clear offline data:', error)
        }
    }, [updateSyncStatus])

    const getQueueItems = useCallback(async (): Promise<SyncQueueItem[]> => {
        try {
            return await syncService.getQueueItems()
        } catch (error) {
            console.warn('Failed to get queue items:', error)
            return []
        }
    }, [])

    useEffect(() => {
        if (!isOfflineDatabaseReady) return
        const unsubscribeStatus = syncService.onSyncStatusChange((status) => {
            setSyncStatus(status)
            setIsSyncing(status.isActive)
        })

        syncService.onSyncProgress((p) => setProgress(p))

        const unsubscribeConflict = syncService.onConflictDetected((conflict) => {
            setConflicts((prev) => {
                const others = prev.filter((c) => c._id !== conflict._id)
                return [...others, conflict]
            })
        })

        // Listen for authentication errors during sync
        const unsubscribeAuthError = syncService.onAuthError((retryCallback) => {
            console.log('Authentication error detected during sync, triggering re-auth flow')
            // This will be handled by the AuthProvider through the ReAuthDialog
            // The retryCallback will be called after successful re-authentication
            if (typeof window !== 'undefined' && (window as any).triggerAuthError) {
                (window as any).triggerAuthError(retryCallback)
            }
        })

            ; (async () => {
                await updateSyncStatus()
                try {
                    const allConflicts = await syncService.getAllConflicts()
                    setConflicts(allConflicts)
                } catch (error) {
                    console.warn('Failed to get initial conflicts:', error)
                    setConflicts([])
                }
            })()

        return () => {
            if (typeof unsubscribeStatus === 'function') unsubscribeStatus()
            if (typeof unsubscribeConflict === 'function') unsubscribeConflict()
            if (typeof unsubscribeAuthError === 'function') unsubscribeAuthError()
        }
    }, [updateSyncStatus, isOfflineDatabaseReady])

    return {
        isSyncing,
        syncStatus,
        conflicts,
        progress,
        addToSyncQueue,
        updateSyncStatus,
        sync,
        syncPatient,
        syncConsultation,
        syncDocument,
        getAllConflicts,
        resolveConflict,
        clearCompletedItems,
        retryFailedItems,
        clearAllOfflineData,
        getQueueItems,
    }
} 